import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { CognitoUser } from 'amazon-cognito-identity-js';
import {
  signIn as cognitoSignIn,
  completeNewPassword,
  signOut as cognitoSignOut,
  getCurrentSession,
  getCurrentUser,
} from './cognitoAuth';
import { setAuthToken } from '../api/awsAuth';

interface NewPasswordChallenge {
  cognitoUser: CognitoUser;
  userAttributes: Record<string, string>;
}

interface AuthContextValue {
  user: CognitoUser | null;
  idToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  newPasswordChallenge: NewPasswordChallenge | null;
  signIn: (username: string, password: string) => Promise<void>;
  submitNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newPasswordChallenge, setNewPasswordChallenge] =
    useState<NewPasswordChallenge | null>(null);

  const applySession = useCallback((token: string) => {
    setUser(getCurrentUser());
    setIdToken(token);
    setAuthToken(token);
    setNewPasswordChallenge(null);
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    getCurrentSession()
      .then((session) => {
        if (session) {
          applySession(session.getIdToken().getJwtToken());
        }
      })
      .finally(() => setIsLoading(false));
  }, [applySession]);

  const signIn = useCallback(async (username: string, password: string) => {
    const result = await cognitoSignIn(username, password);
    if (result.session) {
      applySession(result.session.getIdToken().getJwtToken());
    } else if (result.newPasswordRequired) {
      setNewPasswordChallenge(result.newPasswordRequired);
    }
  }, [applySession]);

  const submitNewPassword = useCallback(
    async (newPassword: string) => {
      if (!newPasswordChallenge) {
        throw new Error('No new password challenge pending');
      }
      const session = await completeNewPassword(
        newPasswordChallenge.cognitoUser,
        newPassword,
        newPasswordChallenge.userAttributes
      );
      applySession(session.getIdToken().getJwtToken());
    },
    [newPasswordChallenge, applySession]
  );

  const signOut = useCallback(() => {
    cognitoSignOut();
    setUser(null);
    setIdToken(null);
    setAuthToken(null);
    setNewPasswordChallenge(null);
  }, []);

  const value: AuthContextValue = {
    user,
    idToken,
    isAuthenticated: !!idToken,
    isLoading,
    newPasswordChallenge,
    signIn,
    submitNewPassword,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
